import { ReactNode, useEffect, useMemo } from 'react';

import { Link, useParams } from 'react-router-dom';

import { Icon } from './icon';
import { ProcessingStatusBadge } from './processing-status-badge';
import { SidebarTrigger } from './shadcn/sidebar';
import { Skeleton } from './shadcn/skeleton';
import { RunSummaryDto } from '../../../../backend/src/matter/subset/dto/run-summary.dto';
import { RunStatus } from '../../../../backend/src/types/types';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubsets } from '@/api/queries/useGetSubsets';
import { isProcessingStatus } from '@/new/lib/pipeline-status';
import { cn } from '@/new/lib/utils';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';

export interface RunWithSubsetName extends RunSummaryDto {
  subsetName: string;
}

export type BreadcrumbItem =
  | { href: string; label: string; active: boolean; iconClass?: string; loading?: boolean }
  | { key: string; render?: ReactNode; loading?: boolean };

export function Breadcrumb({ items = [] }: { items?: BreadcrumbItem[] }) {
  const { matterId } = useParams();
  const setProcessingState = useMatterProcessingStore((state) => state.setProcessingState);

  const { data: matter } = useGetMatterV2(matterId);
  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? undefined;
  const { data: subsets, refetch: refetchSubsets } = useGetSubsets(matterId ?? null);

  // Compute processing state from all cases
  const processingState = useMemo(() => {
    if (!subsets || !matterId) {
      return null;
    }

    // Map runs with their subset information
    const allRunsWithSubset = subsets.flatMap((subset) =>
      (subset.runs ?? []).map((run) => ({
        ...run,
        subsetName: subset.name,
      })),
    );
    const hasCompletedRuns = allRunsWithSubset.some((run) => run?.status === RunStatus.Complete);
    const hasProcessingRuns = allRunsWithSubset.some((run) => run && isProcessingStatus(run.status as RunStatus));

    const processingRuns = allRunsWithSubset.filter((item) => item && isProcessingStatus(item.status as RunStatus));
    // Sort processing runs: awaiting approval first, then by created date (most recent first)
    const sortedProcessingRuns = [...processingRuns].sort((a, b) => {
      const aIsAwaitingApproval = a.status === RunStatus.AwaitingApproval;
      const bIsAwaitingApproval = b.status === RunStatus.AwaitingApproval;

      // Awaiting approval runs come first
      if (aIsAwaitingApproval && !bIsAwaitingApproval) return -1;
      if (!aIsAwaitingApproval && bIsAwaitingApproval) return 1;

      // Within the same category, sort by created date (most recent first)
      const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
      const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
      return dateB - dateA;
    });

    const mostRecentRun = [...allRunsWithSubset].sort((a, b) => {
      const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
      const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
      return dateB - dateA;
    })[0];

    return {
      hasCompletedRuns,
      hasProcessingRuns,
      allProcessingRuns: sortedProcessingRuns,
      mostRecentRun,
    };
  }, [subsets, matterId]);

  useEffect(() => {
    if (!processingState?.hasProcessingRuns) {
      return;
    }
    const intervalId = setInterval(() => {
      refetchSubsets();
    }, 10_000);

    return () => clearInterval(intervalId);
  }, [processingState?.hasProcessingRuns, refetchSubsets]);

  // Save processing state to store
  useEffect(() => {
    if (processingState && matterId) {
      setProcessingState(matterId, processingState);
    }
  }, [processingState, matterId, setProcessingState]);

  return (
    <div
      className="sticky top-0 z-30 flex w-full items-center gap-4 rounded-t-lg border-b border-gray-200 bg-white/75 p-2.5 px-3.5 text-sm text-gray-500 backdrop-blur-lg duration-200"
      data-tour="breadcrumb"
    >
      <SidebarTrigger />

      {(() => {
        // Render up to the first not-yet-ready piece, then show a single skeleton in its place.
        const cutoff = items.findIndex((item) => item.loading);
        const visible = cutoff === -1 ? items : items.slice(0, cutoff);
        const showSkeleton = cutoff !== -1;

        return (
          <>
            {visible.map((item, i) => (
              <div key={'href' in item ? item.href : item.key} className="flex items-center gap-2">
                {'href' in item ? (
                  <Link
                    to={item.href}
                    className={cn([
                      item.active && 'text-gray-800',
                      'inline-flex items-center gap-2 duration-150 hover:text-gray-800',
                    ])}
                  >
                    {item.iconClass && <Icon className={item.iconClass} />}
                    <span className="line-clamp-1 max-w-52 break-all">{item.label}</span>
                  </Link>
                ) : (
                  item.render
                )}

                {(i < visible.length - 1 || showSkeleton) && <span className="pl-1.5 text-gray-300/80">/</span>}
              </div>
            ))}

            {showSkeleton && <Skeleton className="h-3.5 w-28" />}
          </>
        );
      })()}

      {processingState && processingState.hasProcessingRuns && (
        <div className="ml-auto">
          <ProcessingStatusBadge allDocumentsCaseId={allDocumentsCaseId} />
        </div>
      )}
    </div>
  );
}
